import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { resetLocalAddress } from 'services/apiConfig';
import { roomApi } from 'services/RoomService';
import { statusApi } from 'services/StatusService';
import { thingsApi } from 'services/ThingsService';
import { RootState } from 'store';

const debugLog = (message?: unknown, ...optionalParams: unknown[]) =>
  console.log(`[${Date.now()}]`, 'NetworkSlice:', message, ...optionalParams);

export interface NetworkState {
  localConnection: boolean;
  mobileConnected: boolean;
  cloudOnline?: boolean;
  edgeOnline?: boolean;
  localEdgeIPAddress?: string;
  fetchErrorCount: number;
  lastSensorRefreshTimestamp: number;
}

const initialState: NetworkState = {
  localConnection: false,
  mobileConnected: true,
  cloudOnline: true,
  edgeOnline: true,
  fetchErrorCount: 0,
  lastSensorRefreshTimestamp: 0,
};

export interface ChangeNetworkStatusAction {
  connected: boolean;
  connectionType: 'wifi' | 'cellular' | 'none' | 'unknown';
}

export const localEdgeIPAddress = createAsyncThunk<string | undefined, string | undefined>(
  'network/localEdgeIPAddress',
  (args, thunkAPI) => {
    thunkAPI.dispatch(roomApi.endpoints.getMyRooms.initiate()).refetch().unsubscribe();
    return args;
  },
);

export const networkSlice = createSlice({
  name: 'network',
  initialState,
  reducers: {
    changeNetworkStatus: (state, action: PayloadAction<ChangeNetworkStatusAction>) => {
      debugLog('changeNetworkStatus before', JSON.stringify(state, null, 2), action);
      if (action.payload.connectionType === 'wifi') {
        state.localConnection = false;
        state.mobileConnected = true;
        state.cloudOnline = true;
        state.edgeOnline = true;
      } else {
        state.localConnection = false;

        if (action.payload.connected) {
          state.mobileConnected = true;
          state.cloudOnline = true;
          state.edgeOnline = true;
        } else {
          state.mobileConnected = false;
          state.cloudOnline = undefined;
          state.edgeOnline = undefined;
        }
      }
      debugLog('changeNetworkStatus after', JSON.stringify(state, null, 2), action);
    },
    localEdgeIPAddressMissing: (state) => {
      debugLog('localEdgeIPAddressMissing before', JSON.stringify(state, null, 2));
      if (state.localConnection) {
        state.localConnection = false;
        if (state.mobileConnected) {
          state.cloudOnline = true;
          state.edgeOnline = true;
        } else {
          state.cloudOnline = undefined;
          state.edgeOnline = undefined;
        }
      }
      state.localEdgeIPAddress = undefined;
      debugLog('localEdgeIPAddressMissing after', JSON.stringify(state, null, 2));
    },
    localEdgeIPAddressPingFailed: (state) => {
      debugLog('localEdgeIPAddressPingFailed before', JSON.stringify(state, null, 2));
      if (state.localConnection) {
        state.localConnection = false;
        if (state.mobileConnected) {
          state.cloudOnline = true;
          state.edgeOnline = true;
        } else {
          state.cloudOnline = undefined;
          state.edgeOnline = undefined;
        }
      }
      state.localEdgeIPAddress = undefined;
      debugLog('localEdgeIPAddressPingFailed after', JSON.stringify(state, null, 2));
    },
    changeLocalConnectionStatus: (state, action: PayloadAction<boolean>) => {
      debugLog('changeLocalConnectionStatus before', JSON.stringify(state, null, 2), action);
      state.localConnection = action.payload;
      if (action.payload) {
        state.cloudOnline = undefined;
        state.edgeOnline = true;
      } else {
        state.cloudOnline = false;
        state.edgeOnline = false;
      }
      debugLog('changeLocalConnectionStatus after', JSON.stringify(state, null, 2), action);
    },
    increaseFetchErrorCount: (state) => {
      if (state.fetchErrorCount++ >= 3) {
        resetLocalAddress();

        state.localConnection = false;
        state.cloudOnline = false;
        state.edgeOnline = false;
        state.fetchErrorCount = 0;
        debugLog('Resseting localserviceinfo and switch to Local');
      }
      debugLog('[IncreasedFetchErrorCount]:', JSON.stringify(state, null, 2));
    },
    fulfilledRequest: (state) => {
      debugLog('fulfilledRequest before', JSON.stringify(state, null, 2));
      if (!state.localConnection) {
        state.cloudOnline = true;
      } else if (state.localEdgeIPAddress) {
        state.edgeOnline = true;
      }
      debugLog('fulfilledRequest after', JSON.stringify(state, null, 2));
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(localEdgeIPAddress.fulfilled, (state, action: PayloadAction<string | undefined>) => {
        state.localConnection = true;
        state.mobileConnected = true;
        state.cloudOnline = undefined;
        state.edgeOnline = true;
        state.localEdgeIPAddress = action.payload;
      })
      .addMatcher(statusApi.endpoints.getApiStatus.matchFulfilled, (state) => {
        debugLog('[GetApiStatus Fulfilled] before:', JSON.stringify(state, null, 2));
        if (!state.localConnection) {
          state.cloudOnline = true;
        } else if (state.localEdgeIPAddress) {
          state.edgeOnline = true;
        }
        debugLog('[GetApiStatus Fulfilled] after:', JSON.stringify(state, null, 2));
      })
      .addMatcher(statusApi.endpoints.getApiStatus.matchRejected, (state) => {
        debugLog('[GetApiStatus Rejected] before:', JSON.stringify(state, null, 2));
        if (!state.localConnection) {
          state.cloudOnline = state.mobileConnected ? false : undefined;
          state.edgeOnline = state.mobileConnected ? false : undefined;
        } else {
          state.edgeOnline = false;
        }
        debugLog('[GetApiStatus Rejected] after:', JSON.stringify(state, null, 2));
      })
      .addMatcher(thingsApi.endpoints.getAirQualityAllSensors.matchFulfilled, (state, action) => {
        debugLog(
          '[getAirQualityAllSensors Fulfilled] before:',
          JSON.stringify(state, null, 2),
          action,
        );
        if (!state.localConnection) {
          if (!state.lastSensorRefreshTimestamp) {
            state.edgeOnline = true;
          } else {
            if (
              action.payload.timestamp === state.lastSensorRefreshTimestamp ||
              Math.abs(action.payload.timestamp - state.lastSensorRefreshTimestamp) > 90
            ) {
              state.edgeOnline = false;
            } else {
              state.edgeOnline = true;
            }
          }
        }

        state.lastSensorRefreshTimestamp = action.payload.timestamp;
        debugLog('[getAirQualityAllSensors Fulfilled] after:', JSON.stringify(state, null, 2));
      }),
});

export const {
  changeNetworkStatus,
  changeLocalConnectionStatus,
  increaseFetchErrorCount,
  fulfilledRequest,
  localEdgeIPAddressMissing,
  localEdgeIPAddressPingFailed,
} = networkSlice.actions;

export const getNetworkStatus = (state: RootState) => state.network;

export default networkSlice.reducer;
